import Decimal from 'decimal.js';

const ZERO_DECIMAL_CURRENCIES = new Set(['UGX', 'RWF', 'JPY']);

export function currencyDecimals(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 0 : 2;
}

/** Formats as `UGX 1,250,000` (no decimals); other currencies use their standard decimals. */
export function formatMoney(amount: Decimal.Value, currency = 'UGX'): string {
  const decimals = currencyDecimals(currency);
  const value = new Decimal(amount).toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP);
  const negative = value.isNegative();
  const [int = '0', frac] = value.abs().toFixed(decimals).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = frac ? `${grouped}.${frac}` : grouped;
  return `${negative ? '-' : ''}${currency.toUpperCase()} ${body}`;
}

/** DD/MM/YYYY in the Africa/Kampala time zone. */
export function formatDate(input: Date | string | number): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Kampala',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')}`;
}

/**
 * Normalises Ugandan phone numbers to +256XXXXXXXXX.
 * Accepts 0772123456, 772123456, 256772123456, +256 772 123 456. Returns null if invalid.
 * Numbers already in another international format (+254...) are kept if plausible.
 */
export function normalisePhone(raw: string): string | null {
  const cleaned = raw.replace(/[\s\-().]/g, '');
  if (!/^\+?\d+$/.test(cleaned)) return null;
  if (cleaned.startsWith('+')) {
    const intl = cleaned.slice(1);
    return /^[1-9]\d{7,14}$/.test(intl) ? `+${intl}` : null;
  }
  let digits = cleaned;
  if (digits.startsWith('256')) digits = digits.slice(3);
  else if (digits.startsWith('0')) digits = digits.slice(1);
  return /^[1-9]\d{8}$/.test(digits) ? `+256${digits}` : null;
}

export function whatsappLink(phone: string, message: string): string | null {
  const normalised = normalisePhone(phone);
  if (!normalised) return null;
  return `https://wa.me/${normalised.slice(1)}?text=${encodeURIComponent(message)}`;
}
